import { useMemo, useState } from "preact/hooks"
import useSWR from "swr"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { toast } from "@/components/ui/use-toast"
import {
  KrogerProductReviewItem,
  KrogerProductReviewStatus,
  KrogerProductReviewUpdate,
  list_kroger_product_review,
  save_kroger_product_review,
} from "@/api/kroger"
import { SettingsSection } from "../layout"
import { Loader2 } from "lucide-react"

export type ProductReviewFilter = "all" | "pending" | "approved" | "rejected"

interface KrogerProductReviewSectionProps {}

function format_currency(amount: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
}

export function filter_product_review_items(
  items: KrogerProductReviewItem[],
  filter: ProductReviewFilter
): KrogerProductReviewItem[] {
  if (filter === "all") {
    return items
  }
  return items.filter((item: KrogerProductReviewItem) => item.status === filter)
}

export function build_product_review_updates(
  drafts: Record<string, Exclude<KrogerProductReviewStatus, "pending">>,
  items: KrogerProductReviewItem[]
): KrogerProductReviewUpdate[] {
  return Object.entries(drafts)
    .filter(([product_description, status]) => {
      const existing = items.find((item: KrogerProductReviewItem) => item.product_description === product_description)
      return existing != null && existing.status !== status
    })
    .map(([product_description, status]) => ({ product_description, status }))
}

function ProductReviewStatusBadge(props: { status: KrogerProductReviewStatus; draftStatus?: Exclude<KrogerProductReviewStatus, "pending"> }) {
  const status = props.draftStatus ?? props.status
  const className =
    status === "approved"
      ? "bg-green-100 text-green-800"
      : status === "rejected"
        ? "bg-red-100 text-red-800"
        : "bg-amber-100 text-amber-800"
  const label = status === "approved" ? "Approved" : status === "rejected" ? "Wrong brand" : "Needs review"
  return <span class={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>{label}</span>
}

function ProductReviewFilterTabs(props: {
  filter: ProductReviewFilter
  onChange: (filter: ProductReviewFilter) => void
  counts: Record<ProductReviewFilter, number>
}) {
  const tabs: { id: ProductReviewFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "pending", label: "Needs review" },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Wrong brand" },
  ]

  return (
    <div class="flex flex-wrap gap-2">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          class={
            props.filter === tab.id
              ? "rounded-full bg-gray-900 px-3 py-1 text-sm text-white"
              : "rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 hover:bg-gray-200"
          }
          onClick={() => props.onChange(tab.id)}>
          {tab.label} ({props.counts[tab.id]})
        </button>
      ))}
    </div>
  )
}

export function KrogerProductReviewSection(_props: KrogerProductReviewSectionProps) {
  const { data, isLoading, mutate } = useSWR("kroger-product-review", list_kroger_product_review)
  const [filter, setFilter] = useState<ProductReviewFilter>("pending")
  const [drafts, setDrafts] = useState<Record<string, Exclude<KrogerProductReviewStatus, "pending">>>({})
  const [saving, setSaving] = useState(false)

  const items = data?.items ?? []

  const counts = useMemo(() => {
    return {
      all: items.length,
      pending: items.filter((item: KrogerProductReviewItem) => item.status === "pending").length,
      approved: items.filter((item: KrogerProductReviewItem) => item.status === "approved").length,
      rejected: items.filter((item: KrogerProductReviewItem) => item.status === "rejected").length,
    }
  }, [items])

  const filtered_items = useMemo(() => filter_product_review_items(items, filter), [items, filter])

  const pending_updates = useMemo(() => build_product_review_updates(drafts, items), [drafts, items])

  function set_draft_status(product_description: string, status: Exclude<KrogerProductReviewStatus, "pending">) {
    setDrafts((current) => {
      const existing = items.find((item: KrogerProductReviewItem) => item.product_description === product_description)
      if (existing?.status === status) {
        const next = { ...current }
        delete next[product_description]
        return next
      }
      return { ...current, [product_description]: status }
    })
  }

  async function handle_save() {
    if (pending_updates.length === 0 && counts.rejected === 0) {
      toast({ title: "No changes to save" })
      return
    }

    setSaving(true)
    try {
      const response = await save_kroger_product_review(pending_updates)
      setDrafts({})
      await mutate()
      toast({
        title: "Product review saved",
        description: `${response.summary.deductions_marked} deduction(s) marked disputable (${format_currency(response.summary.total_dispute_amount)} total).`,
      })
    } catch (error) {
      toast({
        title: "Failed to save product review",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsSection title="Wrong-Brand Product Review">
      <p class="text-sm text-gray-500 mb-4">
        Review product names parsed from Kroger backup line items. Flag wrong-brand products to mark affected deductions
        disputable and pre-fill dispute messages with the exact charged amounts.
      </p>

      {isLoading ? (
        <div class="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : items.length === 0 ? (
        <div class="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
          No Kroger product names found yet. Product names appear here after Kroger backup CSVs are synced.
        </div>
      ) : (
        <div class="space-y-4">
          <ProductReviewFilterTabs filter={filter} onChange={setFilter} counts={counts} />

          <div class="overflow-x-auto rounded-lg border border-gray-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product name</TableHead>
                  <TableHead class="text-right">Deductions</TableHead>
                  <TableHead class="text-right">Line items</TableHead>
                  <TableHead class="text-right">Total charged</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead class="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered_items.map((item: KrogerProductReviewItem) => {
                  const draft_status = drafts[item.product_description]
                  return (
                    <TableRow key={item.product_description}>
                      <TableCell>
                        <div class="font-medium text-gray-900">{item.product_description}</div>
                        {item.sample_upcs.length > 0 ? (
                          <div class="text-xs text-gray-500">UPC: {item.sample_upcs.join(", ")}</div>
                        ) : null}
                      </TableCell>
                      <TableCell class="text-right">{item.deduction_count}</TableCell>
                      <TableCell class="text-right">{item.backup_row_count}</TableCell>
                      <TableCell class="text-right">{format_currency(item.total_amount)}</TableCell>
                      <TableCell>
                        <ProductReviewStatusBadge status={item.status} draftStatus={draft_status} />
                      </TableCell>
                      <TableCell class="text-right">
                        <div class="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant={draft_status === "approved" || item.status === "approved" ? "default" : "outline"}
                            class="h-8 px-3 text-xs"
                            onClick={() => set_draft_status(item.product_description, "approved")}>
                            Approve
                          </Button>
                          <Button
                            type="button"
                            variant={draft_status === "rejected" || item.status === "rejected" ? "destructive" : "outline"}
                            class="h-8 px-3 text-xs"
                            onClick={() => set_draft_status(item.product_description, "rejected")}>
                            Wrong brand
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>

          <div class="sticky bottom-0 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
            <span class="text-sm text-gray-500">
              {pending_updates.length > 0
                ? `${pending_updates.length} unsaved change(s)`
                : counts.rejected > 0
                  ? `${counts.rejected} wrong-brand product(s) flagged — apply to deductions`
                  : "Select products to approve or flag as wrong brand"}
            </span>
            <Button
              type="button"
              disabled={saving || (pending_updates.length === 0 && counts.rejected === 0)}
              onClick={handle_save}>
              {saving ? "Saving..." : pending_updates.length > 0 ? "Save review" : "Apply to deductions"}
            </Button>
          </div>
        </div>
      )}
    </SettingsSection>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("filter_product_review_items", () => {
    const sample_items: KrogerProductReviewItem[] = [
      {
        product_description: "SANZO SPARKLING",
        status: "pending",
        backup_row_count: 2,
        deduction_count: 1,
        total_amount: 50,
        sample_upcs: [],
      },
      {
        product_description: "LIQUID DEATH",
        status: "rejected",
        backup_row_count: 1,
        deduction_count: 1,
        total_amount: 100,
        sample_upcs: [],
      },
    ]

    it("returns all items for all filter", () => {
      expect(filter_product_review_items(sample_items, "all")).toHaveLength(2)
    })

    it("filters pending items", () => {
      expect(filter_product_review_items(sample_items, "pending")).toHaveLength(1)
    })
  })

  describe("build_product_review_updates", () => {
    const sample_items: KrogerProductReviewItem[] = [
      {
        product_description: "SANZO SPARKLING",
        status: "pending",
        backup_row_count: 2,
        deduction_count: 1,
        total_amount: 50,
        sample_upcs: [],
      },
    ]

    it("builds updates only for changed statuses", () => {
      const updates = build_product_review_updates({ "SANZO SPARKLING": "approved" }, sample_items)
      expect(updates).toEqual([{ product_description: "SANZO SPARKLING", status: "approved" }])
    })

    it("skips unchanged statuses", () => {
      const updates = build_product_review_updates({}, sample_items)
      expect(updates).toEqual([])
    })
  })
}
